import React from 'react';
import { Link } from 'react-router-dom';
import { Stethoscope, ArrowLeft } from 'lucide-react';

const PrivacyPolicyPage = () => {
  return (
    <div className="min-h-screen bg-slate-900">
      {/* Header */}
      <header className="bg-slate-900 border-b border-slate-800 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-lg bg-emerald-500 flex items-center justify-center">
                <Stethoscope className="w-6 h-6 text-white" />
              </div>
              <div className="flex flex-col">
                <span className="font-bold text-lg text-white leading-tight">Trac Jobs Alert</span>
                <span className="text-xs text-emerald-400">For Doctors</span>
              </div>
            </Link>
            <Link to="/" className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors">
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Link>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="text-sm font-medium text-white mb-4">Privacy Policy</h1>
        
        <div className="prose prose-invert prose-slate prose-sm max-w-none space-y-4">
          <section className="space-y-1">
            <p className="text-xs text-slate-300 font-medium mt-3">1. Introduction</p>
            <p className="text-slate-400 text-xs">
              Trac Jobs Alert ("we", "our", "us") operates the tracjobsalert.co.uk website and service. 
              This Privacy Policy explains how we collect, use, disclose, and safeguard your information 
              when you use our job alert service for medical professionals in the United Kingdom.
            </p>
            <p className="text-slate-400 text-xs">
              By using our Service, you agree to the collection and use of information in accordance 
              with this policy.
            </p>
          </section>

          <section className="space-y-1">
            <p className="text-xs text-slate-300 font-medium mt-3">2. Information We Collect</p>
            
            <p className="text-xs text-slate-400">2.1 Personal Information</p>
            <p className="text-slate-400 text-xs">We collect the following personal information:</p>
            <ul className="list-disc list-inside text-slate-400 text-xs space-y-1">
              <li><strong className="text-white">Account Information:</strong> Full name, email address, phone number (optional)</li>
              <li><strong className="text-white">Professional Information:</strong> Current medical grade (e.g., FY1, FY2, Consultant), specialty preferences</li>
              <li><strong className="text-white">Job Preferences:</strong> Preferred locations, occupations, grades, and keywords for job searches</li>
              <li><strong className="text-white">Payment Information:</strong> Processed securely through Stripe; we do not store your full card details</li>
            </ul>

            <p className="text-xs text-slate-400">2.2 Usage Data</p>
            <p className="text-slate-400 text-xs">We automatically collect:</p>
            <ul className="list-disc list-inside text-slate-400 text-xs space-y-1">
              <li>Browser type and version</li>
              <li>Device information</li>
              <li>Pages visited and time spent</li>
              <li>Job alerts created and interactions</li>
              <li>Email open and click rates</li>
            </ul>

            <p className="text-xs text-slate-400">2.3 Cookies and Tracking</p>
            <p className="text-slate-400 text-xs">
              We use cookies and similar tracking technologies to track activity on our Service and 
              hold certain information. You can instruct your browser to refuse all cookies or indicate 
              when a cookie is being sent.
            </p>
          </section>

          <section className="space-y-1">
            <p className="text-xs text-slate-300 font-medium mt-3">3. How We Use Your Information</p>
            <p className="text-slate-400 text-xs">We use the collected information for:</p>
            
            <p className="text-xs text-slate-400">3.1 Service Delivery</p>
            <ul className="list-disc list-inside text-slate-400 text-xs space-y-1">
              <li>Providing personalised job alerts based on your preferences</li>
              <li>Sending email notifications about matching job opportunities</li>
              <li>Managing your account and subscription</li>
              <li>Processing payments through Stripe</li>
              <li>Providing customer support</li>
            </ul>

            <p className="text-xs text-slate-400">3.2 Service Improvement</p>
            <ul className="list-disc list-inside text-slate-400 text-xs space-y-1">
              <li>Analysing usage patterns to improve our service</li>
              <li>Developing new features and functionality</li>
              <li>Conducting research and analytics</li>
            </ul>

            <p className="text-xs text-slate-400">3.3 Communications</p>
            <p className="text-slate-400 text-xs">
              We may occasionally send you service updates, new feature announcements, or promotional 
              communications. You can opt out of marketing communications at any time through your 
              account settings or by using the unsubscribe link in our emails.
            </p>
          </section>

          <section className="space-y-1">
            <p className="text-xs text-slate-300 font-medium mt-3">4. Data Sharing and Disclosure</p>
            <p className="text-slate-400 text-xs">We may share your information with:</p>
            
            <p className="text-xs text-slate-400">4.1 Service Providers</p>
            <ul className="list-disc list-inside text-slate-400 text-xs space-y-1">
              <li><strong className="text-white">Stripe:</strong> Payment processing</li>
              <li><strong className="text-white">Resend:</strong> Email delivery services</li>
              <li><strong className="text-white">Hosting Providers:</strong> Cloud infrastructure</li>
            </ul>

            <p className="text-xs text-slate-400">4.2 Legal Requirements</p>
            <p className="text-slate-400 text-xs">
              We may disclose your information if required by law or in response to valid requests 
              by public authorities.
            </p>
          </section>

          <section className="space-y-1">
            <p className="text-xs text-slate-300 font-medium mt-3">5. Data Retention</p>
            <p className="text-slate-400 text-xs">
              We retain your personal data for as long as your account is active or as needed to 
              provide you services. We will retain and use your information as necessary to comply 
              with legal obligations, resolve disputes, and enforce our agreements.
            </p>
            <ul className="list-disc list-inside text-slate-400 text-xs space-y-1">
              <li><strong className="text-white">Active accounts:</strong> Data retained while subscription is active</li>
              <li><strong className="text-white">Cancelled accounts:</strong> Data retained for 12 months, then anonymised or deleted</li>
              <li><strong className="text-white">Financial records:</strong> Retained for 7 years as required by law</li>
            </ul>
          </section>

          <section className="space-y-1">
            <p className="text-xs text-slate-300 font-medium mt-3">6. Data Security</p>
            <p className="text-slate-400 text-xs">
              We implement appropriate technical and organisational measures to protect your personal 
              data, including:
            </p>
            <ul className="list-disc list-inside text-slate-400 text-xs space-y-1">
              <li>Encryption of data in transit (HTTPS/TLS)</li>
              <li>Secure password hashing</li>
              <li>Regular security assessments</li>
              <li>Access controls and authentication</li>
            </ul>
          </section>

          <section className="space-y-1">
            <p className="text-xs text-slate-300 font-medium mt-3">7. International Transfers</p>
            <p className="text-slate-400 text-xs">
              Your information may be transferred to and processed in countries outside the UK/EEA. 
              We ensure appropriate safeguards are in place for such transfers in compliance with 
              applicable data protection laws.
            </p>
          </section>

          <section className="space-y-1">
            <p className="text-xs text-slate-300 font-medium mt-3">8. Children's Privacy</p>
            <p className="text-slate-400 text-xs">
              Our Service is not intended for individuals under 18 years of age. We do not knowingly 
              collect personal information from children.
            </p>
          </section>

          <section className="space-y-1">
            <p className="text-xs text-slate-300 font-medium mt-3">9. Changes to This Policy</p>
            <p className="text-slate-400 text-xs">
              We may update this Privacy Policy from time to time. We will notify you of any changes 
              by posting the new Privacy Policy on this page and updating the "Last updated" date.
            </p>
          </section>

          <section className="space-y-1">
            <p className="text-xs text-slate-300 font-medium mt-3">10. Contact Us</p>
            <p className="text-slate-400 text-xs">
              If you have any questions about this Privacy Policy, please contact us through our 
              website contact form at tracjobsalert.co.uk/contact
            </p>
          </section>

          <p className="text-slate-500 text-xs text-center pt-4 border-t border-slate-700 mt-4">
            Last updated: February 2026
          </p>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-400 py-8 px-4 border-t border-slate-800">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-sm">© 2026 tracjobsalert.co.uk - All rights reserved</p>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicyPage;
